import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    CHART_SAMPLING_OPTIONS,
    CHART_SAMPLING_TYPE_OPTIONS,
    ChartSamplingFilter,
    ChartSamplingTypeFilter,
} from '../../../constants/data.constants';
import { ParameterRow, ParametersValues } from '../../../models/models';
import { getDeviceParamsChartDataStart } from '../../../store/device/device.action';
import {
    FlexBoxFullWHColumn,
    FlexBoxFullWRowBetween,
} from '../../layout/layout.styles';
import ChartFilters from './chart-filters/chart-filters.component';
import ParamChart from './param-chart/param-chart.component';

const ParamsCHartsList = ({
    deviceId,
    deviceName,
    params,
}: {
    deviceId: string;
    deviceName: string;
    params: ParameterRow[];
}) => {
    const dispatch = useDispatch();

    const [chartsData, setChartsData] = useState<ParametersValues>();
    const [selectedFilter, setSelectedFilter] = useState<ChartSamplingFilter>(
        CHART_SAMPLING_OPTIONS.find((f) => f.key === '5min')!
    );
    const [selectedType, setSelectedType] = useState<ChartSamplingTypeFilter>(
        CHART_SAMPLING_TYPE_OPTIONS[0]
    );
    const lastTimestamp = useRef<number>();

    const getParamsChartData = useCallback(() => {
        const currentTimestamp = moment(new Date()).unix();
        const fromTs = moment(new Date())
            .subtract(selectedFilter.delta, 'seconds')
            .unix();
        dispatch(
            getDeviceParamsChartDataStart(
                deviceId,
                params.map((p) => p.id),
                selectedFilter.zoom,
                fromTs,
                currentTimestamp,
                selectedType?.key ?? null,
                true,
                (data) => {
                    setChartsData(data);
                }
            )
        );
        lastTimestamp.current = currentTimestamp;
    }, [dispatch, deviceId, params, selectedFilter, selectedType]);

    const getParamsChartDataPolling = useCallback(() => {
        if (!lastTimestamp.current) return;
        const currentTimestamp = moment(new Date()).unix();
        const fromTs = lastTimestamp.current;
        dispatch(
            getDeviceParamsChartDataStart(
                deviceId,
                params.map((p) => p.id),
                selectedFilter.zoom,
                fromTs,
                currentTimestamp,
                selectedType?.key ?? null,
                false,
                (data) => {
                    setChartsData((prev) => {
                        if (!prev) return data;
                        const newValues: ParametersValues = Object.assign(
                            {},
                            prev
                        );
                        Object.keys(data).forEach((key) => {
                            data[key].forEach((d) => {
                                const exists = prev[key].find(
                                    (o) => o.ts === d.ts
                                );
                                if (!exists) {
                                    newValues[key].push(d);
                                }
                            });
                        });
                        return newValues;
                    });
                }
            )
        );
        lastTimestamp.current = currentTimestamp;
    }, [dispatch, deviceId, params, selectedFilter, selectedType]);

    const adjustChartData = (
        data: { ts: string; value: number }[],
        paramKey: string
    ) => {
        return data
            .sort((a, b) => a.ts.localeCompare(b.ts))
            .map((d) => {
                const param = params.find((p) => p.id === paramKey)!;
                const type = param.type;
                const weight = param.weight ?? 1;
                let value = d.value;
                if (type === 'number') {
                    value = value * weight;
                }
                return {
                    ts: moment(d.ts + 'Z').valueOf(),
                    value: value,
                };
            });
    };

    const handleFilterChange = (filter: ChartSamplingFilter) => {
        setSelectedFilter(filter);
    };

    const handleFilterTypeChange = (filter: ChartSamplingTypeFilter) => {
        setSelectedType(filter);
    };

    useEffect(() => {
        getParamsChartData();
        const intervalId = setInterval(() => {
            if (!lastTimestamp.current) return;
            getParamsChartDataPolling();
        }, 5000);

        return () => clearInterval(intervalId);
    }, [getParamsChartData, getParamsChartDataPolling]);

    return (
        <FlexBoxFullWHColumn gap={2} overflow={'hidden'}>
            <FlexBoxFullWRowBetween alignItems={'center'}>
                <Box width={1 / 2}>
                    <ChartFilters
                        selectedFilter={selectedFilter}
                        selectedType={selectedType}
                        onFilterChange={handleFilterChange}
                        onFilterTypeChange={handleFilterTypeChange}
                    />
                </Box>
                <Box width={1 / 2}>
                    <Typography fontSize={25} textAlign={'right'}>
                        {deviceName}
                    </Typography>
                </Box>
            </FlexBoxFullWRowBetween>
            <FlexBoxFullWHColumn gap={2} overflow={'auto'} pb={2}>
                {chartsData &&
                    Object.keys(chartsData).map((key) => {
                        const param = params.find((p) => p.id === key);
                        return (
                            <ParamChart
                                key={key}
                                param={param!}
                                delta={selectedFilter.delta}
                                chartData={adjustChartData(
                                    chartsData[key],
                                    key
                                )}
                            />
                        );
                    })}
            </FlexBoxFullWHColumn>
        </FlexBoxFullWHColumn>
    );
};

export default ParamsCHartsList;
